<template>
  <div>
    <v-container fluid grid-list-md>
      <v-layout>
        <v-flex>
          <v-btn @click="back()">Back</v-btn>
        </v-flex>
      </v-layout>
    </v-container>

    <v-container v-if="loading" fluid fill-height justify-center align-center>
      <v-progress-circular indeterminate color="indigo darken-4"></v-progress-circular>
    </v-container>

    <v-container v-else fluid grid-list-md>
      <v-layout wrap>
        <v-flex wrap xs12>
          <v-card>
            <v-card-title>
              <div class="how-we-want">
                <div>
                  <h1>{{ tournament.name }} <span> - {{ tournament.gender ? 'Women' : 'Men' }}</span></h1>
                  <v-switch label="Live matches only" @click="toggleLiveFilter" :input-value="liveFilter"></v-switch>
                </div>
                <v-btn right @click="exportMatches">Export matches</v-btn>
              </div>
            </v-card-title>

            <v-data-table :headers="headers" :custom-sort="customSort" :pagination.sync="pagination" :items="filteredMatches" :loading="loading" hide-actions>
              <template slot="items" slot-scope="{ item }">
                <tr @click="rowClick(item)" style="cursor: pointer">
                  <td class="hidden-sm-and-down">{{ `${item.noInTournament}`.padStart(3, '0') }}</td>
                  <td>{{ formatDate(`${item.utcDate} ${item.utcTime}`) }}</td>
                  <td class="hidden-sm-and-down">{{ item.court }}</td>
                  <td class="hidden-sm-and-down">{{ item.city }}</td>
                  <td class="hidden-sm-and-down">{{ item.roundName }}</td>
                  <td>
                    <div style="display:flex; align-items: center">
                      <template v-if="item.teamA">
                        <f-flag :code="item.teamA.federationCode" :height="20"></f-flag>
                        <span style="margin-right: 4px">{{ item.teamA.name }}</span>
                        <span v-if="item.roundPhase === 4">[{{ item.teamA.positionInMainDraw }}]</span>
                        <span v-else-if="item.roundPhase === 3">[Q{{ item.teamA.positionInQualification }}]</span>
                      </template>
                      <template v-else>
                        {{ computePhase(item.teamAType) }}
                      </template>
                    </div>
                  </td>
                  <td>
                   <div style="display:flex; align-items: center">
                      <template v-if="item.teamB">
                        <f-flag :code="item.teamB.federationCode" :height="20"></f-flag>
                        <span style="margin-right: 4px">{{ item.teamB.name }}</span>
                        <span v-if="item.roundPhase === 4">[{{ item.teamB.positionInMainDraw }}]</span>
                        <span v-else-if="item.roundPhase === 3">[Q{{ item.teamB.positionInQualification }}]</span>
                      </template>
                      <template v-else>
                        {{ computePhase(item.teamBType) }}
                      </template>
                    </div>
                  </td>
                  <td class="hidden-sm-and-down">
                    <strong style="margin-right: 10px">{{ item.matchResultText }}</strong>
                    <span>{{ item.setsResultsText }}</span>
                  </td>
                  <td class="hidden-sm-and-down">{{ calculateDuration(item.durationSet1 + item.durationSet2 + item.durationSet3)  }}</td>
                  <td class="hidden-sm-and-down">{{ status[item.status] }}</td>
                  <td class="hidden-sm-and-down">
                    <v-btn
                      color="blue-grey"
                      class="white--text"
                      v-if="item.noDocumentReport"
                      small
                      :href="`https://www.fivb.org/vis2009/getdocument.asmx?no=${item.noDocumentReport}`"
                      target="_blank"
                    >
                      Match Report
                      <v-icon right dark>cloud_download</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card>
        </v-flex>

        <v-flex wrap xs7>
          <statistics-by-player :tournament-id="$route.params.id"></statistics-by-player>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import { format } from 'date-fns'
import { Request, QueryBuilder } from '@fivb/sdk'

import Flag from '@/components/Flag'
import MatchStatus from '@/matchStatus'
import StatisticsByPlayer from './TournamentMatchList/StatisticsByPlayer/StatisticsByPlayer'

export default {
  data: () => ({
    updateListTimeoutId: null,
    loading: true,
    selectedPhase: null,
    phaseSelector: [],
    poolsData: [],
    tournament: {},
    matches: [],
    status: MatchStatus,
    poolPagination: {
      sortBy: 'position',
      rowsPerPage: -1,
    },
    poolsHeaders: [
      { text: 'Rank', value: 'rankText', sortable: false, class: 'text-xs-center' },
      { text: 'Name', value: 'teamName', sortable: false, },
      { text: 'Won', value: 'matchesWon', sortable: false, class: 'text-xs-center' },
      { text: 'Lost', value: 'matchesLost', sortable: false, class: 'text-xs-center' },
      { text: 'Total', value: 'matches', sortable: false, class: 'text-xs-center' },
      { text: '', value: 'sep1', sortable: false, class: 'text-xs-center' },
      { text: 'Points', value: 'matchPoints', sortable: false, class: 'text-xs-center' },
      { text: '3-0', value: 'matchesAt30', sortable: false, class: 'text-xs-center' },
      { text: '3-1', value: 'matchesAt31', sortable: false, class: 'text-xs-center' },
      { text: '3-2', value: 'matchesAt32', sortable: false, class: 'text-xs-center' },
      { text: '2-3', value: 'matchesAt23', sortable: false, class: 'text-xs-center' },
      { text: '1-3', value: 'matchesAt13', sortable: false, class: 'text-xs-center' },
      { text: '0-3', value: 'matchesAt03', sortable: false, class: 'text-xs-center' },
      { text: '', value: 'sep2', sortable: false, },
      { text: 'Sets Won', value: 'setsWon', sortable: false, class: 'text-xs-center' },
      { text: 'Sets Lost', value: 'setsLost', sortable: false, class: 'text-xs-center' },
      { text: 'Sets Ratio', value: 'setsRatioText', sortable: false, class: 'text-xs-center' },
      { text: '', value: 'sep3', sortable: false, },
      { text: 'Points Won', value: 'pointsWon', sortable: false, class: 'text-xs-center' },
      { text: 'Points Lost', value: 'pointsLost', sortable: false, class: 'text-xs-center' },
      { text: 'Points Ratio', value: 'pointsRatioText', sortable: false, class: 'text-xs-center' },
    ],
    pagination: {
      sortBy: 'dateTimeUtc',
      rowsPerPage: -1,
    },
    headers: [
      { text: 'No', value: 'noInTournament', sortable: true, class: 'hidden-sm-and-down', },
      { text: 'Date', value: 'dateTimeUtc', sortable: true, },
      { text: 'Court', value: 'court', sortable: true, class: 'hidden-sm-and-down', },
      { text: 'City', value: 'city', sortable: true, class: 'hidden-sm-and-down', },
      { text: 'Phase', value: 'poolName', sortable: true, class: 'hidden-sm-and-down', },
      { text: 'Team A', value: 'teamA.name', sortable: true, },
      { text: 'Team B', value: 'teamB.name', sortable: true, },
      { text: 'Score', value: 'matchResultText', sortable: true, class: 'hidden-sm-and-down', },
      { text: 'Duration', value: 'durationSet1', sortable: false, class: 'hidden-sm-and-down', },
      { text: 'Status', value: 'status', sortable: true, class: 'hidden-sm-and-down', },
      { text: 'Document', value: 'noDocumentReport', sortable: false, class: 'hidden-sm-and-down', },
    ],
  }),

  components: {
    StatisticsByPlayer,
    'f-flag': Flag,
  },

  beforeDestroy () {
    if (this.updateListTimeoutId) {
      clearTimeout(this.updateListTimeoutId)
    }
  },

  async created () {
    await this.fetchTournament()
    await this.fetchMatchList()

    this.loading = false
  },

  watch: {
    async selectedPhase () {
      const request = new Request('GetBeachRoundRanking')
        .addAttribute('Fields', 'Position RankText TeamCode TeamName MatchPoints Matches MatchesWon MatchesLost MatchesAt30 MatchesAt31 MatchesAt32 MatchesAt23 MatchesAt13 MatchesAt03 SetsWon SetsLost SetsRatioText PointsWon PointsLost PointsRatioText')
        .addAttribute('NoPool', this.selectedPhase)
        .toString()

      const { data } = await this.fetchData(request)
      this.poolsData = data
    }
  },

  computed: {
    // tournament () {
    //   return this.$store.state.tournamentsList[this.$route.params.id].tournament
    // },

    // matches () {
    //   return this.$store.state.tournamentsList[this.$route.params.id].matches
    // },

    liveFilter () {
      return this.$store.state.tournamentsList.liveFilter
    },

    filteredMatches: function () {
      if (!this.liveFilter) {
        return this.matches
      }

      return this.matches.filter((match) => {
        return match.status >= 2 && match.status <= 12
      })
    },
  },

  methods: {
    calculateDuration (duration) {
      const hours = Math.floor(duration / 3600)

      const minutes = duration > 3600
        ? Math.floor((duration % 3600) / 60)
        : Math.floor(duration / 60)

      return `${hours}:${minutes.toString().padStart(2, 0)}`
    },

    async fetchTournament () {
      const tournament = await this.fetchData(this.generateTournamentQuery())

      this.tournament = tournament.data
    },

    async fetchMatchList () {
      const matches = await this.fetchData(this.generateMatchesQuery())

      this.$store.commit('updateOrCreateTournament', {
        code: this.$route.params.id,
        localState: {
          version: matches.version,
          matches: matches.data,
          tournament: undefined,
        },
      })

      this.matches = matches.data
      this.updateListTimeoutId = setTimeout(() => this.fetchMatchList(), 20000)
    },

    customSort (items, index, isDesc) {
      items.sort((a, b) => {
        // If we want to sort the date
        if (index === 'dateTimeUtc') {
          const aRealDate = `${a.utcDate} ${a.utcTime ? a.utcTime : '00:00:00'}`
          const bRealDate = `${b.utcDate} ${b.utcTime ? b.utcTime : '00:00:00'}`

          if (!isDesc) {
            return new Date(aRealDate) - new Date(bRealDate)
          }

          return new Date(bRealDate) - new Date(aRealDate)
        }

        // Default sorting
        if (!isDesc) {
          return a[index] < b[index] ? -1 : 1
        }

        return b[index] < a[index] ? -1 : 1
      })

      return items
    },

    computePhase (phase) {
      if (phase.type === 'Draw') {
        return 'Draw'
      }

      if (phase.type === 'Bye') {
        return 'Bye'
      }

      const no = phase.noMatch || phase.no || phase.position

      if (phase.type === 'Seed') {
        return `${phase.type} #${no}`
      }

      return `${phase.type} of ${no}`
    },

    toggleLiveFilter () {
      this.$store.commit('toggleTournamentLiveFilter')
    },

    back () {
      this.$router.push({ name: 'TournamentList' })
    },

    rowClick ({ no }) {
      this.$router.push({ name: 'MatchLive', params: { id: no } })
    },

    formatDate (date) {
      return format(date, 'MM/DD HH:mm')
    },

    generateTournamentQuery () {
      return new QueryBuilder('BeachTournament')
        .find(this.$route.params.id, ['Name', 'ShortNameOrName', 'Gender', 'NoEvent'])
    },

    generateMatchesQuery () {
      // const version = this.$store.state.tournamentsList[this.$route.params.id]
      //   ? this.$store.state.tournamentsList[this.$route.params.id].version
      //   : 0

      return new QueryBuilder('BeachMatch')
        .filterBy('NoTournament', this.$route.params.id)
        .with('TeamA')
        .with('TeamB')
        .fetch(
          ['No', 'City', 'RoundName', 'RoundPhase', 'Court', 'DurationSet1', 'DurationSet2', 'DurationSet3', 'NoDocumentReport', 'NoInTournament', 'TeamAType', 'TeamBType', 'UtcDate', 'UtcTime', 'Status', 'MatchResultText', 'SetsResultsText'],
          0
        )
    },

    async exportMatches() {
      const query = `<Request Type="GetBeachMatchList" Fields="No"><Filter NoEvent="${this.tournament.noEvent}" /></Request>`
      const matches = await this.fetchData(query)

      const getDocumentQuery = `<Request Type="ExportBeachMatchList" NoExport="12" ExportFormat="2" Numbers="${matches.data.map(m => m.no).join(' ')}" />`
      const token = await this.fetchData(getDocumentQuery)
      window.open(`https://www.fivb.org/Vis2009/Vis2009/GetDocument.asmx?Token=${token.data.token}`)
    },

    // generatePoolListQuery () {
    //   return new QueryBuilder('BeachRound')
    //     .filterBy('NoTournament', this.$route.params.id)
    //     .filterBy('ForRanking', true)
    //     .fetch(['No', 'Name'])
    // },

    fetchData (query) {
      return fetch(process.env.VUE_APP_FIVB_API_URL, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-store',
        headers: new Headers({
          'Content-Type': 'application/xml',
          'Accept': 'application/json',
          'X-FIVB-App-ID': '3b665ad61056495ab5514b81eb0562d8'
        }),
        body: query,
      }).then(response => response.json())
    },
  },
}
</script>

<style>
.how-we-want {
  display: flex;
  justify-content: space-between;
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/TournamentMatchList.vue